import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity, View as RNView, Platform, Modal as RNModal, Pressable } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { MaterialCommunityIcons, Feather } from "@expo/vector-icons";
import { useIsFocused } from "@react-navigation/native";
import { replaceDataValues } from "../../utils/common";
import { View } from "../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { LabelBannerWithSearchTags } from "../../components/custom/Banners/LabelBannerWithSearchTags";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import { default as Modal } from "../../components/custom/Modal/index";
import Spinner from "../../components/shared/Spinner";
import useColorScheme from "../../hooks/useColorScheme";
import { userClaimInformation } from "../../models/usersModel";
import { validSignedURLWithExt } from "../../utils/validateSignedUrl";

import _ from "lodash";

import {
  getDelivery
} from "../../services/deliveryService";
import { headers, mdHeaders, smHeaders, stockTransferPhoneHeaders } from "./utils/deliveryHeaders";
import {
  nestedStockTransferHeaders as deliveryHeader,
  mdNestedStockTransferHeaders as deliveryMdHeaders,
  smNestedHeaders as deliverySmHeaders,
} from "./utils/deliveryDetailsHeaders";
import { deliveryModel } from "../../models/reportsModel";
import {
  filterBasedOnAnyKey,
} from "../../utils/searchInArrays";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { useSelector } from "react-redux";
import { handleTableLoader } from "../../utils/common";
import { sortColumns } from "../../utils/sortColumns";

export default function StockTransferScreen({ route, navigation }: any) {
  const scrollRef = useRef<ScrollView>(null);
  const isFocused = useIsFocused();
  const colorScheme = useColorScheme();

  const columnsToNotHaveThousandsSeparator: Array<string> = ["Document #", "Reference", "Document # ", "DocNum", "documentNo"];
  const dataToBeConverted: object = {
    /*{ "searchString" : "replaceValue" }*/
    "Dispatched": "Shipped",
    "Not Confirmed": "Pending for Payment"
  };


  const { userInfo } = useSelector((state: any) => state.configurations);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [deliveryPageLevelList, setDeliveryPageLevelList] = useState<
    Array<deliveryModel>
  >([]);


  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const { userRoles } = useSelector((state: any) => state.configurations);

  const [stockTransferListFilterCategoryList] =
    useState(["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "Today", "Next 7 days", "Next 14 days", "Next 30 days", "Transfer In", "Transfer Out"]);

  // Multi-select filter arrays
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<"date" | "state" | "type" | null>(null);

  const dateFilterOptions = ["All", "Today", "Next 7 days", "Next 14 days", "Next 30 days"];
  const stateFilterOptions = ["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS"];
  const typeFilterOptions = ["all", "Transfer In", "Transfer Out"];

  const { stockTransferList, isStockTransferListLoaded } = useSelector(
    (state: any) => state.stockTransfer
  );

  const { stockTransferItemsList } = useSelector((state: any) => state.items);

  const [selected, setSelected] = useState<deliveryModel>(
    {} as unknown as deliveryModel
  );

  //Table & Nested Table Headers
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );

  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      deliveryHeader
    );

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const [, setContentOffSet] = useState<number>(0);

  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("stock_transfer_hidden_columns").then((stored: string | null) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch (e) { }
      }
    });
    AsyncStorage.getItem("stock_transfer_column_order").then((stored) => {
      if (stored) {
        try {
          setColumnOrder(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  const getAllHeaders = () => {
    const base = isLargeDevice ? headers : isMediumDevice ? mdHeaders : isMobileDevice ? stockTransferPhoneHeaders : smHeaders;
    const { CustomerPortalOrderNo, ...rest } = base as any;
    return rest;
  };

  const getOrderedKeys = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key) => [key, baseHeaders[key]] as [string, Record<string, string | number | boolean>]);
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h: any) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]: any) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? (totalOriginalFlex / totalVisibleFlex) : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]: any) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allHeaders = getAllHeaders();
    const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    newKeys.push("");
    setColumnOrder(newKeys);
    AsyncStorage.setItem("stock_transfer_column_order", JSON.stringify(newKeys));
  }, [columnOrder]);

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("stock_transfer_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);

  const filterDeliveryByCategory = (
    _deliveryMode: Array<deliveryModel>,
    _selectedDates: string[],
    _selectedStates: string[],
    _selectedTypes: string[]
  ) => {
    const today = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 1
    );
    const tmw = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const Nxt7Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);
    const Nxt14Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14);
    const Nxt30Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30);

    return _deliveryMode.filter((delivery: deliveryModel) => {
      const reqDate = new Date(
        new Date(delivery.RequestDate).getFullYear(),
        new Date(delivery.RequestDate).getMonth(),
        new Date(delivery.RequestDate).getDate()
      );

      const checkDateFilter = (d: string) => {
        switch (d) {
          case "Today": return reqDate >= today && reqDate <= tmw;
          case "Next 7 days": return reqDate >= today && reqDate <= Nxt7Days;
          case "Next 14 days": return reqDate >= today && reqDate <= Nxt14Days;
          case "Next 30 days": return reqDate >= today && reqDate <= Nxt30Days;
          default: return false;
        }
      };

      const dateOk = _selectedDates.length === 0 || _selectedDates.some(d => checkDateFilter(d));
      const stateOk = _selectedStates.length === 0 || _selectedStates.some(s => delivery.State === s);
      const typeOk = _selectedTypes.length === 0 || _selectedTypes.some(t => delivery.DocType === t);

      return dateOk && stateOk && typeOk;
    });
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedDeliveryList: Array<deliveryModel> = sortColumns(
      deliveryPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setDeliveryPageLevelList(filterDeliveryByCategory(_sortedDeliveryList, selectedDates, selectedStates, selectedTypes));
  };

  /**
   * TODO: NESTED TABLE SORTED Is Disabled,
   * enable when sorting on nested table feature is requested
   */
  // const onHandleNestedSortColumn = (
  //   columnKey: string,
  //   columnParentIdentifier: string
  // ) => {
  //   const _sortedItemsList: Array<itemsModal> = sortColumns(
  //     nestedItemsPageLevelList,
  //     columnKey,
  //     nestedTableHeaders,
  //     setNestedTableHeaders
  //   );

  //   setNestedItemsPageLevelList(_sortedItemsList);
  // };

  const onHandleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  const onHandleRowClick = (row: deliveryModel) => { };

  const fadeTableLoader = (setIsSearched: (e: boolean) => void) => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleRemoveRow = (containerId: string) => { };

  const onHandleSearch = (searchText: string) => {
    displayTableLoader(setIsSearched);

    let searchedDelivery: Array<deliveryModel> = [];
    if (searchText.trim() !== "") {
      /**
       * When SearchBy: Delivery, enter into if block
       * otherwise go for else block
       */
      if (!_isDisplayViewOn) {
        const filteredDelivery = filterBasedOnAnyKey(
          extractIdenticalDelivery(),
          searchText
        );

        searchedDelivery = feedItemsInDelivery(filteredDelivery);
      } else {
        /**
         * Steps to search for items within containers
         * 1: Extract all items from containers & union them
         * 2: Search by keyword in the list of items extracted from containers
         * 3: Group by container_number on the searchedItems
         * 4: Remove the duplicates from array of container_numbers
         * 5: filter the unique containers from the identicalFeededDeliveryWithItems()
         *    & return the list of containers with items matching the search text
         */

        //Step 1
        const itemsFromDelivery = _.map(
          identicalFeededDeliveryWithItems(),
          "items"
        );
        const flattenItems = _.flatten(itemsFromDelivery);

        //Step 2
        const searchedItems = filterBasedOnAnyKey(flattenItems, searchText);

        //Step 3
        const groupedDelivery = Object.keys(
          _.groupBy(searchedItems, "DocNum")
        );

        //Step 4
        const uniqueDelivery = _.uniq(groupedDelivery);

        //Step 5
        const filteredDelivery = identicalFeededDeliveryWithItems().filter(
          (delivery) => uniqueDelivery.includes(delivery.DocNum)
        );

        searchedDelivery = filteredDelivery;
      }
      setDeliveryPageLevelList(filterDeliveryByCategory(searchedDelivery, selectedDates, selectedStates, selectedTypes));
    }

    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  };

  const onHandleScrollToBase = () => {
    scrollRef.current?.scrollTo({
      x: 0,
      y: 0,
      animated: true,
    });
  };

  useEffect(() => {
    validSignedURLWithExt(
      selected.PicturName || "",
      "label",
      setImageLoading,
      setSelectedImage
    );
  }, [selected]);

  const ModalContent = () => {
    const _excludedKeys = [
      "BaseReference",
      "BatchAttribute1",
      "BatchAttribute2",
      "BatchAttribute3",
      "U_CashCusName",
      "U_CashCusPhone",
      "U_PMX_BAV1",
      "U_PMX_BAV2",
      "U_PMX_BAV3",
      "LastUpdate",
      "OrderQtyPCS",
      "SQRef.",
      "PaymentTerm",
      "RemainingQtyPCS"
    ];

    const _orderedKeys = [
      "DocNum",
      "DocType",
      "ItemCode",
      "ProductGroup",
      "CustomerCode",
      "CustomerRef",
      "DeliveryAddress",
      "OrderDate",
      "RequestDate",
      "DelivrdQty",
      "OrderQty",
      "RemainingQty",
      "ShipToCode",
      "ShippingType",
      "State",
      "Warehouse",
      "SalesPerson",
      "ProcessBy"
    ];

    const _specialLabels = {
      DocType: {
        oldLabel: "Document",
        newLabel: "Document Type",
      },
      ItemCode: {
        oldLabel: "ItemCode",
        newLabel: "Item Code",
      },
      ProductGroup: {
        oldLabel: "ProductGroup",
        newLabel: "Product Group",
      },
      CustomerCode: {
        oldLabel: "CustomerCode",
        newLabel: "Customer Code",
      },
      CustomerRef: {
        oldLabel: "CustomerCode",
        newLabel: "Customer Ref#",
      },
      DeliveryAddress: {
        oldLabel: "DeliveryAddress",
        newLabel: "Delivery Address",
      },
      OrderDate: {
        oldLabel: "OrderDate",
        newLabel: "Order Date",
      },
      RequestDate: {
        oldLabel: "RequestDate",
        newLabel: "Request Date",
      },
      DelivrdQty: {
        oldLabel: "DelivrdQty",
        newLabel: "Delivery Qty",
      },
      OrderQty: {
        oldLabel: "OrderQty",
        newLabel: "Order Qty",
      },
      RemainingQty: {
        oldLabel: "RemainingQty",
        newLabel: "Remaining Qty",
      },
      SalesPerson: {
        oldLabel: "SalesPerson",
        newLabel: "Sales",
      },
      ProcessBy: {
        oldLabel: "ProcessBy",
        newLabel: "Operator",
      },
    };

    return (
      <Modal.ModalContent
        title={selected.ItemCode || ""}
        subTitle={selected.Description}
        icon="folder"
        uri={selectedImage}
        loading={isImageLoading}
        childern={
          <Modal.ModalBody
            listData={selected}
            excludedKeys={_excludedKeys}
            renameList={headers}
            orderedKeys={_orderedKeys}
            specialLabels={_specialLabels}
            columnsToNotHaveThousandSeparator={columnsToNotHaveThousandsSeparator}
          />
        }
      />
    );
  };

  const extractIdenticalDelivery = () => {
    //process both containers and items list to return filtered containers list
    const _stockTransferItemsList: Array<deliveryModel> = stockTransferList;
    const groupDelivery = _.groupBy(
      _stockTransferItemsList,
      (item) => item.DocNum
    );
    // check here
    return _stockTransferItemsList as Array<deliveryModel>;
  };

  const feedItemsInDelivery = (delivery: deliveryModel[]) => {
    const _stockTransferItemsList: Array<deliveryModel> = [...stockTransferList];

    return delivery.map((_delivery) => {
      return {
        ..._delivery,
        items: _stockTransferItemsList.filter(
          (item: deliveryModel) =>
            item.DocNum === _delivery.DocNum
        ),
      };
    });
  };

  const identicalFeededDeliveryWithItems = (): Array<deliveryModel> => {
    return feedItemsInDelivery(extractIdenticalDelivery());
  };

  const displayTableLoader = (setIsSearched: React.Dispatch<React.SetStateAction<boolean>>) => {
    handleTableLoader(true, false, setIsSearched);
  };


  useEffect(() => {
    setRefresh(true);
  }, [_isDisplayViewOn]);

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;
    // Avoid keep refreshing table when item is selected
    if (isMounted) {
      if (deliveryPageLevelList.length === 0 || isRefresh) {
        displayTableLoader(setIsSearched);
        // filters reset on refresh — selectedDates/selectedStates/selectedTypes remain user choice
        getDelivery(
          "StockTransferReport",
          "",
          _userInfo.team,
          _userInfo.zone_info || "",
          "stock_transfer_report",
          tableHeaders
        );
      }
    }

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh, deliveryPageLevelList.length]);

  useEffect(() => {
    const baseHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(baseHeaders));

    setNestedTableHeaders(
      isLargeDevice
        ? deliveryHeader
        : isMediumDevice
          ? deliveryMdHeaders
          : deliverySmHeaders
    );
  }, [hiddenColumns, columnOrder, isLargeDevice, isMediumDevice, isSmallDevice, isMobileDevice]);

  useEffect(() => {
    // _setFilterCategory([])
    const transferOut = [
      ...filterDeliveryByCategory(identicalFeededDeliveryWithItems(), [], [], ["Transfer Out"]).reduce((map, obj) => {
        if (!map.has(obj.DocNum)) map.set(obj.DocNum, obj);
        return map;
      }, new Map)
        .values()
    ];

    const transferIn = [
      ...filterDeliveryByCategory(identicalFeededDeliveryWithItems(), [], [], ["Transfer In"]).reduce((map, obj) => {
        if (!map.has(obj.DocNum)) map.set(obj.DocNum, obj);
        return map;
      }, new Map)
        .values()
    ];


    const unique = [...transferIn, ...transferOut];
    setDeliveryPageLevelList(filterDeliveryByCategory(unique, selectedDates, selectedStates, selectedTypes));


    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  }, [stockTransferList, stockTransferItemsList, selectedDates, selectedStates, selectedTypes]);

  return (
    <ScrollView
      style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      onScroll={onHandleScroll}
      ref={scrollRef}
    >
      <View
        style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      >
        <View
          style={[
            styles.container,
            {
              backgroundColor: colorScheme === "light" ? "#f2f2f2" : "#f2f2f2",
            },
          ]}
        >
          <View
            style={[
              styles.containerContent,
              {
                width: isLargeDevice
                  ? "100%"
                  : isMediumDevice
                    ? "100%"
                    : "100%",
              },
            ]}
          >
            {isStockTransferListLoaded ? (
              <View>
                {!(isMobileDevice || isSmallDevice) ? (
                  <>
                    <View style={stFilterStyles.quickFilterBar}>
                      <View style={{ flex: 1, flexDirection: "row", alignItems: "center", overflow: "hidden" }}>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={stFilterStyles.quickFilterChipsContainer}>
                          <Text style={stFilterStyles.quickFilterLabel}>Quick Filter</Text>

                          {/* Date Dropdown */}
                          <TouchableOpacity
                            style={[
                              stFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "date" && stFilterStyles.mobileDropdownButtonActive,
                              { marginRight: 8 }
                            ]}
                            onPress={() => setMobileFilterOpen(mobileFilterOpen === "date" ? null : "date")}
                          >
                            <Text style={stFilterStyles.mobileDropdownLabel}>Date: </Text>
                            <Text style={stFilterStyles.mobileDropdownValue}>
                              {selectedDates.length === 0
                                ? "All"
                                : selectedDates.length === 1
                                  ? selectedDates[0]
                                  : `${selectedDates.length} Selected`}
                            </Text>
                            <Text style={stFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "date" ? "▲" : "▼"}</Text>
                          </TouchableOpacity>

                          {/* State Dropdown */}
                          <TouchableOpacity
                            style={[
                              stFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "state" && stFilterStyles.mobileDropdownButtonActive,
                              { marginRight: 8 }
                            ]}
                            onPress={() => setMobileFilterOpen(mobileFilterOpen === "state" ? null : "state")}
                          >
                            <Text style={stFilterStyles.mobileDropdownLabel}>State: </Text>
                            <Text style={stFilterStyles.mobileDropdownValue}>
                              {selectedStates.length === 0
                                ? "All"
                                : selectedStates.length === 1
                                  ? selectedStates[0]
                                  : `${selectedStates.length} Selected`}
                            </Text>
                            <Text style={stFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "state" ? "▲" : "▼"}</Text>
                          </TouchableOpacity>

                          {/* Type Dropdown */}
                          <TouchableOpacity
                            style={[
                              stFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "type" && stFilterStyles.mobileDropdownButtonActive,
                            ]}
                            onPress={() => setMobileFilterOpen(mobileFilterOpen === "type" ? null : "type")}
                          >
                            <Text style={stFilterStyles.mobileDropdownLabel}>Type: </Text>
                            <Text style={stFilterStyles.mobileDropdownValue}>
                              {selectedTypes.length === 0
                                ? "All"
                                : selectedTypes.length === 1
                                  ? selectedTypes[0]
                                  : `${selectedTypes.length} Selected`}
                            </Text>
                            <Text style={stFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "type" ? "▲" : "▼"}</Text>
                          </TouchableOpacity>
                        </ScrollView>
                      </View>
                      <View style={stFilterStyles.quickFilterSearchSection}>
                        <LabelBannerWithSearchBar
                          titleName={`Total Delivery: ${deliveryPageLevelList.length}`}
                          handleSearch={onHandleSearch}
                          setRefresh={setRefresh}
                          isResetFields={isRefresh}
                          
                          //Export Data to Excel                          
                          dataToExport={deliveryPageLevelList.map((delivery) => {
                            const formatToDDMMYYYY = (dStr: any) => {
                              if (!dStr) return "";
                              const date = new Date(dStr);
                              if (isNaN(date.getTime())) return String(dStr).split(" ")[0];
                              return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
                            };
                            return {
                              "State": delivery.State,
                              "Warehouse": delivery.Warehouse,
                              "Customer Name": delivery.CustomerName,
                              "Document #": delivery.DocNum,
                              "Document": delivery.DocType,
                              "Order Status": delivery.OrderStatus,
                              "Request Date": formatToDDMMYYYY(delivery.RequestDate),
                              "Shipping Type": delivery.ShippingType,
                              "Shipping Code": delivery.ShipToCode,
                              "Sales": delivery.SalesPerson,
                              "Operator": delivery.ProcessBy,
                            };
                          })}
                          fileName="stockTransferReport"
                          sheetName="Stock Transfer Report"

                          extraControls={!(isSmallDevice || isMobileDevice) ?
                            <RNView>
                              <TouchableOpacity
                                ref={settingsBtnRef}
                                style={{
                                  width: isSmallDevice || isMobileDevice ? 34 : 40,
                                  height: isSmallDevice || isMobileDevice ? 34 : 40,
                                  borderRadius: isSmallDevice || isMobileDevice ? 8 : 10,
                                  backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderWidth: 1,
                                  borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                                }}
                                onPress={() => {
                                  if (settingsBtnRef.current) {
                                    settingsBtnRef.current.measureInWindow(
                                      (x: number, y: number, width: number, height: number) => {
                                        setSettingsBtnPos({ x, y, width, height });
                                        setColumnSettingsOpen(!columnSettingsOpen);
                                      }
                                    );
                                  } else {
                                    setColumnSettingsOpen(!columnSettingsOpen);
                                  }
                                }}
                              >
                                <MaterialCommunityIcons
                                  name="cog-outline"
                                  size={20}
                                  color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                                />
                              </TouchableOpacity>
                              <RNModal
                                visible={columnSettingsOpen}
                                transparent={true}
                                animationType="fade"
                                onRequestClose={() => setColumnSettingsOpen(false)}
                              >
                                <Pressable
                                  style={{
                                    flex: 1,
                                    backgroundColor: "rgba(0,0,0,0.15)",
                                    ...((isSmallDevice || isMobileDevice)
                                      ? { justifyContent: "center", alignItems: "center", padding: 20 }
                                      : {}),
                                  }}
                                  onPress={() => setColumnSettingsOpen(false)}
                                >
                                  <Pressable
                                    style={{
                                      ...((isSmallDevice || isMobileDevice)
                                        ? { width: "100%", maxWidth: 320 }
                                        : {
                                          position: "absolute",
                                          top: settingsBtnPos.y + settingsBtnPos.height + 6,
                                          left: settingsBtnPos.x,
                                          minWidth: 220,
                                        }),
                                      backgroundColor: "#fff",
                                      borderRadius: 12,
                                      borderWidth: 1,
                                      borderColor: "#E2E8F0",
                                      paddingVertical: 10,
                                      shadowColor: "#000",
                                      shadowOffset: { width: 0, height: 8 },
                                      shadowOpacity: 0.15,
                                      shadowRadius: 20,
                                      elevation: 10,
                                    }}
                                    onPress={(e) => e.stopPropagation()}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        fontWeight: "700",
                                        color: "#6B7280",
                                        paddingHorizontal: 16,
                                        paddingBottom: 8,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      Show / Hide Columns
                                    </Text>
                                    <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                                    <TouchableOpacity
                                      style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        paddingVertical: 8,
                                        paddingHorizontal: 16,
                                      }}
                                      onPress={() => {
                                        const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                                        if (hiddenColumns.length === 0) {
                                          // Hide all
                                          const updated = [...allKeys];
                                          setHiddenColumns(updated);
                                          AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify(updated));
                                        } else {
                                          // Show all
                                          setHiddenColumns([]);
                                          AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify([]));
                                        }
                                      }}
                                    >
                                      <View
                                        style={{
                                          width: 20,
                                          height: 20,
                                          borderRadius: 4,
                                          borderWidth: 1.5,
                                          borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                                          backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          marginRight: 10,
                                        }}
                                      >
                                        {hiddenColumns.length === 0 && (
                                          <Feather name="check" size={13} color="#fff" />
                                        )}
                                      </View>
                                      <Text
                                        style={{
                                          fontSize: 14,
                                          color: "#111827",
                                          fontWeight: "700",
                                        }}
                                      >
                                        Select All
                                      </Text>
                                    </TouchableOpacity>
                                    <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                                    <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                                      {getOrderedKeys(getAllHeaders()).map((key) => {
                                        if (key === "") return null;
                                        const value = getAllHeaders()[key];
                                        if (!value) return null;
                                        const isVisible = !hiddenColumns.includes(key);
                                        const isDragging = dragActiveKey === key;
                                        const isDragOver = dragOverKey === key;
                                        return (
                                          <RNView
                                            key={key}
                                            {...{ dataSet: { columnkey: key } } as any}
                                            style={{
                                              flexDirection: "row",
                                              alignItems: "center",
                                              paddingVertical: 8,
                                              paddingHorizontal: 16,
                                              opacity: isDragging ? 0.35 : 1,
                                              backgroundColor: isDragOver ? "#EFF6FF" : "transparent",
                                              borderTopWidth: isDragOver ? 2 : 0,
                                              borderTopColor: "#56B7FD",
                                            }}
                                          >
                                            <Pressable
                                              onPressIn={() => startColumnDrag(key)}
                                              style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                            >
                                              <Feather name="menu" size={14} color="#9CA3AF" />
                                            </Pressable>
                                            <TouchableOpacity
                                              style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                              onPress={() => toggleColumn(key)}
                                            >
                                              <RNView
                                                style={{
                                                  width: 20,
                                                  height: 20,
                                                  borderRadius: 4,
                                                  borderWidth: 1.5,
                                                  borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                                  backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                                  justifyContent: "center",
                                                  alignItems: "center",
                                                  marginRight: 10,
                                                }}
                                              >
                                                {isVisible && (
                                                  <Feather name="check" size={13} color="#fff" />
                                                )}
                                              </RNView>
                                              <Text
                                                style={{
                                                  fontSize: 14,
                                                  color: isVisible ? "#111827" : "#9CA3AF",
                                                  fontWeight: isVisible ? "600" : "400",
                                                }}
                                              >
                                                {(value as any).name}
                                              </Text>
                                            </TouchableOpacity>
                                          </RNView>
                                        );
                                      })}
                                    </ScrollView>
                                    {columnOrder.length > 0 && (
                                      <>
                                        <RNView style={{ height: 1, backgroundColor: "#F1F5F9", marginTop: 4 }} />
                                        <TouchableOpacity
                                          style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            paddingVertical: 10,
                                            paddingHorizontal: 16,
                                          }}
                                          onPress={resetColumnOrder}
                                        >
                                          <Feather name="rotate-ccw" size={13} color="#6B7280" />
                                          <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                            Reset Order
                                          </Text>
                                        </TouchableOpacity>
                                      </>
                                    )}
                                  </Pressable>
                                </Pressable>
                              </RNModal>
                            </RNView>
                            : undefined}
                        />
                      </View>
                    </View>

                    {/* Expanded Dropdown Content */}
                    <View style={{ paddingHorizontal: 16 }}>
                      {mobileFilterOpen === "date" && (
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[stFilterStyles.filterChipRow, { paddingTop: 0, paddingBottom: 8 }]}>
                          {["All", "Today", "Next 7 days", "Next 14 days", "Next 30 days"].map((d, i) => {
                            const isActive = d === "All" ? selectedDates.length === 0 : selectedDates.includes(d);
                            return (
                              <TouchableOpacity key={`date-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                                onPress={() => {
                                  if (d === "All") { setSelectedDates([]); }
                                  else {
                                    const n = isActive ? selectedDates.filter(x => x !== d) : [...selectedDates, d];
                                    setSelectedDates(n);
                                  }
                                }}>
                                <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{d}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      )}

                      {mobileFilterOpen === "state" && (
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[stFilterStyles.filterChipRow, { paddingTop: 0, paddingBottom: 8 }]}>
                          {["All", "NSW", "VIC", "QLD", "WA", "SA", "TAS"].map((s, i) => {
                            const isActive = s === "All" ? selectedStates.length === 0 : selectedStates.includes(s);
                            return (
                              <TouchableOpacity key={`state-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                                onPress={() => {
                                  if (s === "All") { setSelectedStates([]); }
                                  else {
                                    const n = isActive ? selectedStates.filter(x => x !== s) : [...selectedStates, s];
                                    setSelectedStates(n);
                                  }
                                }}>
                                <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{s}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      )}

                      {mobileFilterOpen === "type" && (
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[stFilterStyles.filterChipRow, { paddingTop: 0, paddingBottom: 8 }]}>
                          {["All", "Transfer In", "Transfer Out"].map((t, i) => {
                            const isActive = t === "All" ? selectedTypes.length === 0 : selectedTypes.includes(t);
                            return (
                              <TouchableOpacity key={`type-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                                onPress={() => {
                                  if (t === "All") { setSelectedTypes([]); }
                                  else {
                                    const n = isActive ? selectedTypes.filter(x => x !== t) : [...selectedTypes, t];
                                    setSelectedTypes(n);
                                  }
                                }}>
                                <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{t}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      )}
                    </View>
                  </>
                ) : (
                  <>
                    <View style={{ padding: 10, paddingBottom: 0 }}>
                      <LabelBannerWithSearchBar
                        titleName={`Total Delivery: ${deliveryPageLevelList.length}`}
                        handleSearch={onHandleSearch}
                        setRefresh={setRefresh}
                        isResetFields={isRefresh}
                        extraControls={!(isSmallDevice || isMobileDevice) ?
                          <RNView>
                            <TouchableOpacity
                              ref={settingsBtnRef}
                              style={{
                                width: isSmallDevice || isMobileDevice ? 34 : 40,
                                height: isSmallDevice || isMobileDevice ? 34 : 40,
                                borderRadius: isSmallDevice || isMobileDevice ? 8 : 10,
                                backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                                justifyContent: "center",
                                alignItems: "center",
                                borderWidth: 1,
                                borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                              }}
                              onPress={() => {
                                if (settingsBtnRef.current) {
                                  settingsBtnRef.current.measureInWindow(
                                    (x: number, y: number, width: number, height: number) => {
                                      setSettingsBtnPos({ x, y, width, height });
                                      setColumnSettingsOpen(!columnSettingsOpen);
                                    }
                                  );
                                } else {
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                }
                              }}
                            >
                              <MaterialCommunityIcons
                                name="cog-outline"
                                size={20}
                                color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                              />
                            </TouchableOpacity>
                            <RNModal
                              visible={columnSettingsOpen}
                              transparent={true}
                              animationType="fade"
                              onRequestClose={() => setColumnSettingsOpen(false)}
                            >
                              <Pressable
                                style={{
                                  flex: 1,
                                  backgroundColor: "rgba(0,0,0,0.15)",
                                  ...((isSmallDevice || isMobileDevice)
                                    ? { justifyContent: "center", alignItems: "center", padding: 20 }
                                    : {}),
                                }}
                                onPress={() => setColumnSettingsOpen(false)}
                              >
                                <Pressable
                                  style={{
                                    ...((isSmallDevice || isMobileDevice)
                                      ? { width: "100%", maxWidth: 320 }
                                      : {
                                        position: "absolute",
                                        top: settingsBtnPos.y + settingsBtnPos.height + 6,
                                        left: settingsBtnPos.x,
                                        minWidth: 220,
                                      }),
                                    backgroundColor: "#fff",
                                    borderRadius: 12,
                                    borderWidth: 1,
                                    borderColor: "#E2E8F0",
                                    paddingVertical: 10,
                                    shadowColor: "#000",
                                    shadowOffset: { width: 0, height: 8 },
                                    shadowOpacity: 0.15,
                                    shadowRadius: 20,
                                    elevation: 10,
                                  }}
                                  onPress={(e) => e.stopPropagation()}
                                >
                                  <Text
                                    style={{
                                      fontSize: 12,
                                      fontWeight: "700",
                                      color: "#6B7280",
                                      paddingHorizontal: 16,
                                      paddingBottom: 8,
                                      textTransform: "uppercase",
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Show / Hide Columns
                                  </Text>
                                  <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                                  <TouchableOpacity
                                    style={{
                                      flexDirection: "row",
                                      alignItems: "center",
                                      paddingVertical: 8,
                                      paddingHorizontal: 16,
                                    }}
                                    onPress={() => {
                                      const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                                      if (hiddenColumns.length === 0) {
                                        // Hide all
                                        const updated = [...allKeys];
                                        setHiddenColumns(updated);
                                        AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify(updated));
                                      } else {
                                        // Show all
                                        setHiddenColumns([]);
                                        AsyncStorage.setItem("stock_transfer_hidden_columns", JSON.stringify([]));
                                      }
                                    }}
                                  >
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        borderWidth: 1.5,
                                        borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                                        backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 10,
                                      }}
                                    >
                                      {hiddenColumns.length === 0 && (
                                        <Feather name="check" size={13} color="#fff" />
                                      )}
                                    </View>
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: "#111827",
                                        fontWeight: "700",
                                      }}
                                    >
                                      Select All
                                    </Text>
                                  </TouchableOpacity>
                                  <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                                  <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                                    {getOrderedKeys(getAllHeaders()).map((key) => {
                                      if (key === "") return null;
                                      const value = getAllHeaders()[key];
                                      if (!value) return null;
                                      const isVisible = !hiddenColumns.includes(key);
                                      const isDragging = dragActiveKey === key;
                                      const isDragOver = dragOverKey === key;
                                      return (
                                        <RNView
                                          key={key}
                                          {...{ dataSet: { columnkey: key } } as any}
                                          style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            paddingVertical: 8,
                                            paddingHorizontal: 16,
                                            opacity: isDragging ? 0.35 : 1,
                                            backgroundColor: isDragOver ? "#EFF6FF" : "transparent",
                                            borderTopWidth: isDragOver ? 2 : 0,
                                            borderTopColor: "#56B7FD",
                                          }}
                                        >
                                          <Pressable
                                            onPressIn={() => startColumnDrag(key)}
                                            style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                          >
                                            <Feather name="menu" size={14} color="#9CA3AF" />
                                          </Pressable>
                                          <TouchableOpacity
                                            style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                            onPress={() => toggleColumn(key)}
                                          >
                                            <RNView
                                              style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: 4,
                                                borderWidth: 1.5,
                                                borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                                backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginRight: 10,
                                              }}
                                            >
                                              {isVisible && (
                                                <Feather name="check" size={13} color="#fff" />
                                              )}
                                            </RNView>
                                            <Text
                                              style={{
                                                fontSize: 14,
                                                color: isVisible ? "#111827" : "#9CA3AF",
                                                fontWeight: isVisible ? "600" : "400",
                                              }}
                                            >
                                              {(value as any).name}
                                            </Text>
                                          </TouchableOpacity>
                                        </RNView>
                                      );
                                    })}
                                  </ScrollView>
                                  {columnOrder.length > 0 && (
                                    <>
                                      <RNView style={{ height: 1, backgroundColor: "#F1F5F9", marginTop: 4 }} />
                                      <TouchableOpacity
                                        style={{
                                          flexDirection: "row",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          paddingVertical: 10,
                                          paddingHorizontal: 16,
                                        }}
                                        onPress={resetColumnOrder}
                                      >
                                        <Feather name="rotate-ccw" size={13} color="#6B7280" />
                                        <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                          Reset Order
                                        </Text>
                                      </TouchableOpacity>
                                    </>
                                  )}
                                </Pressable>
                              </Pressable>
                            </RNModal>
                          </RNView>
                          : undefined}
                      />
                    </View>
                    <View style={{ paddingHorizontal: 10, paddingTop: 4, paddingBottom: 10 }}>
                      <View style={{ paddingHorizontal: 6 }}>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={stFilterStyles.filterChipRow}>
                          <Text style={{ fontWeight: "700", fontSize: 13, color: "#374151", marginRight: 10, alignSelf: "center" }}>Quick Filter</Text>

                          {/* Date Dropdown */}
                          <TouchableOpacity
                            style={[
                              stFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "date" && stFilterStyles.mobileDropdownButtonActive,
                              { marginRight: 8 }
                            ]}
                            onPress={() => setMobileFilterOpen(mobileFilterOpen === "date" ? null : "date")}
                          >
                            <Text style={stFilterStyles.mobileDropdownLabel}>Date: </Text>
                            <Text style={stFilterStyles.mobileDropdownValue}>
                              {selectedDates.length === 0
                                ? "All"
                                : selectedDates.length === 1
                                  ? selectedDates[0]
                                  : `${selectedDates.length} Selected`}
                            </Text>
                            <Text style={stFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "date" ? "▲" : "▼"}</Text>
                          </TouchableOpacity>

                          {/* State Dropdown */}
                          <TouchableOpacity
                            style={[
                              stFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "state" && stFilterStyles.mobileDropdownButtonActive,
                              { marginRight: 8 }
                            ]}
                            onPress={() => setMobileFilterOpen(mobileFilterOpen === "state" ? null : "state")}
                          >
                            <Text style={stFilterStyles.mobileDropdownLabel}>State: </Text>
                            <Text style={stFilterStyles.mobileDropdownValue}>
                              {selectedStates.length === 0
                                ? "All"
                                : selectedStates.length === 1
                                  ? selectedStates[0]
                                  : `${selectedStates.length} Selected`}
                            </Text>
                            <Text style={stFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "state" ? "▲" : "▼"}</Text>
                          </TouchableOpacity>

                          {/* Type Dropdown */}
                          <TouchableOpacity
                            style={[
                              stFilterStyles.mobileDropdownButton,
                              mobileFilterOpen === "type" && stFilterStyles.mobileDropdownButtonActive,
                            ]}
                            onPress={() => setMobileFilterOpen(mobileFilterOpen === "type" ? null : "type")}
                          >
                            <Text style={stFilterStyles.mobileDropdownLabel}>Type: </Text>
                            <Text style={stFilterStyles.mobileDropdownValue}>
                              {selectedTypes.length === 0
                                ? "All"
                                : selectedTypes.length === 1
                                  ? selectedTypes[0]
                                  : `${selectedTypes.length} Selected`}
                            </Text>
                            <Text style={stFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "type" ? "▲" : "▼"}</Text>
                          </TouchableOpacity>
                        </ScrollView>

                        {/* Expanded Dropdown Content */}
                        {mobileFilterOpen === "date" && (
                          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[stFilterStyles.filterChipRow, { paddingTop: 0, paddingBottom: 8 }]}>
                            {["All", "Today", "Next 7 days", "Next 14 days", "Next 30 days"].map((d, i) => {
                              const isActive = d === "All" ? selectedDates.length === 0 : selectedDates.includes(d);
                              return (
                                <TouchableOpacity key={`date-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                                  onPress={() => {
                                    if (d === "All") { setSelectedDates([]); }
                                    else {
                                      const n = isActive ? selectedDates.filter(x => x !== d) : [...selectedDates, d];
                                      setSelectedDates(n);
                                    }
                                  }}>
                                  <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{d}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                        )}

                        {mobileFilterOpen === "state" && (
                          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[stFilterStyles.filterChipRow, { paddingTop: 0, paddingBottom: 8 }]}>
                            {["All", "NSW", "VIC", "QLD", "WA", "SA", "TAS"].map((s, i) => {
                              const isActive = s === "All" ? selectedStates.length === 0 : selectedStates.includes(s);
                              return (
                                <TouchableOpacity key={`state-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                                  onPress={() => {
                                    if (s === "All") { setSelectedStates([]); }
                                    else {
                                      const n = isActive ? selectedStates.filter(x => x !== s) : [...selectedStates, s];
                                      setSelectedStates(n);
                                    }
                                  }}>
                                  <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{s}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                        )}

                        {mobileFilterOpen === "type" && (
                          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[stFilterStyles.filterChipRow, { paddingTop: 0, paddingBottom: 8 }]}>
                            {["All", "Transfer In", "Transfer Out"].map((t, i) => {
                              const isActive = t === "All" ? selectedTypes.length === 0 : selectedTypes.includes(t);
                              return (
                                <TouchableOpacity key={`type-${i}`} style={[stFilterStyles.filterButton, isActive && stFilterStyles.activeFilterButton]}
                                  onPress={() => {
                                    if (t === "All") { setSelectedTypes([]); }
                                    else {
                                      const n = isActive ? selectedTypes.filter(x => x !== t) : [...selectedTypes, t];
                                      setSelectedTypes(n);
                                    }
                                  }}>
                                  <Text style={isActive ? stFilterStyles.activeFilterText : stFilterStyles.filterText}>{t}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                        )}
                      </View>
                    </View>
                  </>
                )}
                <DataTableWithPagination
                  headers={tableHeaders}
                  rows={replaceDataValues(deliveryPageLevelList, dataToBeConverted)}
                  itemsPerPage={20}
                  isDataLoading={isSearched}
                  useModal={true}
                  nestedHeader={nestedTableHeaders}
                  isRowsExpandable={true}
                  setModalVisible={setModalVisible}
                  handleRowClick={onHandleRowClick}
                  setSelected={setSelected}
                  isToExpandAllRows={_isDisplayViewOn}
                  rowKeyValue="DocNum"
                  nestedItemKeyValue="items"
                  isSubDetail={true}
                  isDetail={false}
                  isEdit={false}
                  isDelete={false}
                  columnsToNotHaveThousandSeparator={columnsToNotHaveThousandsSeparator}
                  handleSortColumn={onHandleSortColumn}
                  handleDeleteRow={function (id: string | number): void {
                    throw new Error("Function not implemented.");
                  }} />
              </View>
            ) : (
              <View>
                <Spinner
                  size="small"
                  color="#74BBFF"
                  styles={{ flexDirection: "row", margin: "20%" }}
                />
              </View>
            )}
          </View>
        </View>
        <Modal.ModalContainer
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          childern={ModalContent}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
});

const stFilterStyles = StyleSheet.create({
  quickFilterBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingTop: 8,
    paddingBottom: 4,
    paddingHorizontal: 10,
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: "#374151",
    marginRight: 10,
  },
  quickFilterSearchSection: {
    flexShrink: 0,
    minWidth: 350,
  },
  singleFilterRow: {
    width: "100%",
    borderBottomWidth: 1,
    borderColor: "darkgrey",
    backgroundColor: "white",
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: "700",
    color: "#111827",
    marginRight: 10,
    minWidth: 36,
  },
  filterChipRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingRight: 8,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  activeFilterButton: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500",
  },
  activeFilterText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
});